import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { firstValueFrom, Subscription, interval } from 'rxjs';
import { startWith, switchMap } from 'rxjs/operators';
import { ListDTO } from 'src/app/interfaces/list';
import { UserDTO } from 'src/app/interfaces/user';
import { ListService } from 'src/app/services/list.service';
import { TokenStorageService } from 'src/app/services/token-storage.service';
import { createListItem, ListItemDTO } from 'src/app/interfaces/listItem';
import { ListItemService } from 'src/app/services/list-item.service';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { EditListComponent } from '../edit-list/edit-list.component';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { CdkDragDrop, moveItemInArray, DragDropModule } from '@angular/cdk/drag-drop';
import { MatSelectModule } from '@angular/material/select';
import { MatMenuModule } from '@angular/material/menu';

@Component({
  selector: 'app-lists',
  standalone: true,
  imports: [CommonModule, FormsModule, MatFormFieldModule, MatInputModule, MatIconModule, MatButtonModule, MatCheckboxModule, MatSelectModule, DragDropModule,
    MatMenuModule],
  templateUrl: './lists.component.html',
  styleUrl: './lists.component.scss'
})
export class ListsComponent implements OnInit {

  user!: UserDTO;
  isLoggedIn = false;

  allLists: ListDTO[] = [];

  listsWithCalendar: ListDTO[] = [];
  standaloneLists: ListDTO[] = [];
  sharedLists: ListDTO[] = [];

  searchTerm: string = '';
  selectedList: ListDTO | null = null;
  listItems: ListItemDTO[] = [];
  newItemDescription: string = '';
  editingItemId: number | null = null;
  editingText: string = '';
  sortBy: 'position' | 'alphabetical' = 'position';
  private refreshSubscription?: Subscription;

  constructor(
    public matDialog: MatDialog,
    private router: Router,
    private tokenStorageService: TokenStorageService,
    private listService: ListService,
    private listItemService: ListItemService,
    private sanitizer: DomSanitizer
  ) { }

  async ngOnInit() {
    this.isLoggedIn = !!this.tokenStorageService.getToken();
    if (!this.isLoggedIn) {
      this.router.navigate(['home']);
      return;
    }

    this.user = this.tokenStorageService.getUser();
    await this.loadLists();

    this.startAutoRefresh();
  }

  ngOnDestroy() {
    this.stopAutoRefresh();
  }

  private startAutoRefresh() {
    // Ververs elke 10.000 ms (10 sec)
    this.refreshSubscription = interval(10000).subscribe(() => {
      // Alleen verversen als er een lijst gekozen is en de gebruiker NIET aan het typen/editten is
      if (this.selectedList && this.editingItemId === null) {
        this.loadListItemsSilent(this.selectedList.id);
      }
    });
  }

  private stopAutoRefresh() {
    if (this.refreshSubscription) {
      this.refreshSubscription.unsubscribe();
    }
  }

  // Stille achtergrond-update van de lijstitems
  private async loadListItemsSilent(listId: number) {
    try {
      const items = await firstValueFrom(this.listItemService.getListItems(listId));
      // Update alleen als de gebruiker niet tussentijds is begonnen met editten
      if (this.editingItemId === null) {
        this.listItems = items;
      }
    } catch (error) {
      console.error('Error auto-refreshing list items:', error);
    }
  }

  async loadLists() {
    this.allLists = await firstValueFrom(this.listService.getLists());
    this.applyFilter();
    this.sharedLists = await firstValueFrom(this.listService.getSharedLists());
  }

  private applyFilter(): void {
    const filtered = this.allLists.filter(list =>
      list.description.toLowerCase().includes(this.searchTerm)
    );

    this.listsWithCalendar = filtered.filter(l => l.calendarId && l.calendarId > -1);
    this.standaloneLists = filtered.filter(l => !l.calendarId || l.calendarId <= -1);
  }

  async selectList(list: ListDTO) {
    this.selectedList = list;
    // Laad de opgeslagen sorteervoorkeur voor deze specifieke lijst
    this.sortBy = this.getSortPreference(list.id);
    await this.loadListItems(list.id);
  }

  async loadListItems(listId: number) {
    try {
      this.listItems = await firstValueFrom(this.listItemService.getListItems(listId));
    } catch (error) {
      console.error('Error fetching list items:', error);
      this.listItems = [];
    }
  }

  onSearch(query: string): void {
    this.searchTerm = query.toLowerCase().trim();
    this.applyFilter();
  }

  clearSearch(inputElement: HTMLInputElement): void {
    inputElement.value = '';
    this.searchTerm = '';
    this.applyFilter();
  }

  createList() {
    this.addEditList(null);
  }

  editList(list: ListDTO, event: Event) {
    event.stopPropagation();
    this.addEditList(list);
  }

  addEditList(list: ListDTO) {
    const dialogConfig = new MatDialogConfig();
    // The user can't close the dialog by clicking outside its body
    dialogConfig.disableClose = false;
    dialogConfig.id = "editListComponent";
    dialogConfig.panelClass = "squaredDialog";
    dialogConfig.data = list ? { ...list } : { description: '', calendarIntegrationId: null, calendarId: null };
    dialogConfig.width = "450px";
    const modalDialog = this.matDialog.open(EditListComponent, dialogConfig);
    modalDialog.afterClosed().subscribe(data => {
      if (data && data.list) {
        this.loadLists();
      }
    });

  }

  async deleteList(list: ListDTO) {
    const data = await firstValueFrom(this.listService.deleteList(list));
    this.loadLists();
  }

  async shareList(list: ListDTO, emailAddress: string) {
    const data = await firstValueFrom(this.listService.shareList(list.id, emailAddress));
  }

  startEdit(item: ListItemDTO): void {
    this.editingItemId = item.id;
    this.editingText = item.description;
  }

  cancelEdit(): void {
    this.editingItemId = null;
    this.editingText = '';
  }

  saveEdit(item: ListItemDTO): void {
    if (this.editingText.trim() && this.editingText !== item.description) {
      item.description = this.editingText.trim();
      this.listItemService.saveListItem(item.id, createListItem(item.listId, item.checked, item.description, item.deadline, item.taskId, new Date(), item.position)).subscribe();
    }
    this.editingItemId = null;
  }

  get completedItems(): ListItemDTO[] {
    return this.listItems.filter(item => item.checked);
  }

  get activeItems(): ListItemDTO[] {
    return this.listItems.filter(item => !item.checked);
  }

  get completionPercentage(): number {
    if (this.listItems.length === 0) return 0;
    return Math.round((this.completedItems.length / this.listItems.length) * 100);
  }

async addItem() {
  if (!this.newItemDescription.trim() || !this.selectedList) return;
  const newPosition = this.listItems.length + 1;
  const data = await firstValueFrom(this.listItemService.saveListItem(-1, createListItem(this.selectedList.id, false, this.newItemDescription, null, null, new Date(), newPosition)));
  this.listItems.push(data);
  this.newItemDescription = '';
}

  async toggleItemStatus(item: ListItemDTO) {
    item.checked = !item.checked;
    const data = await firstValueFrom(this.listItemService.saveListItem(item.id, createListItem(item.listId, item.checked, item.description, item.deadline, item.taskId, new Date(), item.position)));
  }

  async deleteItem(item: ListItemDTO) {
    this.listItems = this.listItems.filter(i => i.id !== item.id);
    const data = await firstValueFrom(this.listItemService.deleteListItem(item))
  }

  formatDescription(description: string): SafeHtml {
    if (!description) return '';

    // Regex om URLs (http, https, www) te detecteren
    const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/g;

    const formattedText = description.replace(urlRegex, (url) => {
      const href = url.startsWith('http') ? url : `https://${url}`;
      return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="item-link" onclick="event.stopPropagation()">${url}</a>`;
    });

    return this.sanitizer.bypassSecurityTrustHtml(formattedText);

  }

  get sortedActiveItems(): ListItemDTO[] {
    const items = [...this.activeItems];
    if (this.sortBy === 'alphabetical') {
      return items.sort((a, b) => a.description.localeCompare(b.description));
    }
    // Standaard op positie / id
    return items.sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  }

  setSortBy(mode: 'position' | 'alphabetical') {
    this.sortBy = mode;
    if (this.selectedList) {
      this.saveSortPreference(this.selectedList.id, mode);
    }
  }

  // Handler voor het verslepen van een item
  onItemDrop(event: CdkDragDrop<ListItemDTO[]>) {
    // Alleen verslepen toestaan als we op 'position' staan
    if (this.sortBy !== 'position') return;

    // Haal de huidige gesorteerde lijst op
    const items = this.sortedActiveItems;

    // Verplaats de elementen lokaal in de array
    moveItemInArray(items, event.previousIndex, event.currentIndex);

    // Update de position properties en sla op in backend
    //TODO do not update the entire object, but just the position
    items.forEach(async (item, index) => {
      item.position = index + 1;
      const data = await firstValueFrom(this.listItemService.updateListItemPosition(item.id, item.position));
    });
  }

  // Sla de sorteervoorkeur van een lijst op in localStorage
  private saveSortPreference(listId: number, mode: 'position' | 'alphabetical') {
    localStorage.setItem(`list_sort_${listId}`, mode);
  }

  // Haal de sorteervoorkeur op voor een lijst (default is 'position')
  private getSortPreference(listId: number): 'position' | 'alphabetical' {
    const saved = localStorage.getItem(`list_sort_${listId}`);
    return (saved === 'alphabetical' || saved === 'position') ? saved : 'position';
  }
}
